import { useCallback, useMemo, useRef } from 'react'

import { Box, Card, Typography } from '@mui/material'

import { Axis, AxisLeft } from '@visx/axis'
import { localPoint } from '@visx/event'
import { GridColumns, GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { ParentSize } from '@visx/responsive'
import { scaleLinear } from '@visx/scale'
import { Circle } from '@visx/shape'
import { TooltipWithBounds, useTooltip } from '@visx/tooltip'
import { voronoi } from '@visx/voronoi'
import { extent } from 'd3-array'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object[]} props.data
 * @param {string} props.data[].name
 * @param {number} props.data[].x
 * @param {number} props.data[].y
 * @param {string} props.data[].color
 * @param {number} props.height
 * @param {number} props.width
 * @param {string} props.xLabel
 * @param {string} props.yLabel
 */
function Chart({ width, height, data, xLabel, yLabel }) {
  const margin = { top: 30, left: 60, right: 50, bottom: 50 }

  const svgRef = useRef(null)
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipOpen,
    tooltipTop = 0,
    tooltipLeft = 0,
  } = useTooltip()

  const x = (d) => d.x
  const y = (d) => d.y
  const name = (d) => d.name
  const color = (d) => d.color

  const innerWidth = Math.max(0, width - margin.left - margin.right)
  const innerHeight = Math.max(0, height - margin.top - margin.bottom)

  const xScale = scaleLinear({
    range: [margin.left, innerWidth + margin.left],
    domain: extent(data, x),
  })
  const yScale = scaleLinear({
    range: [innerHeight + margin.top, margin.top],
    domain: extent(data, y),
  })

  const voronoiLayout = useMemo(
    () =>
      voronoi({
        x: (d) => xScale(x(d)) ?? 0,
        y: (d) => yScale(y(d)) ?? 0,
        width,
        height,
      })(data),
    [data, width, height, xScale, yScale],
  )

  const handleMouseMove = useCallback(
    (event) => {
      if (!svgRef.current) return

      const point = localPoint(svgRef.current, event)
      if (!point) return
      const neighborRadius = 100
      const closest = voronoiLayout.find(point.x, point.y, neighborRadius)
      if (closest) {
        showTooltip({
          tooltipLeft: xScale(x(closest.data)),
          tooltipTop: yScale(y(closest.data)),
          tooltipData: closest.data,
        })
      }
    },
    [xScale, yScale, showTooltip, voronoiLayout],
  )

  const handleMouseLeave = useCallback(() => {
    hideTooltip()
  }, [hideTooltip])

  const TooltipDataItem = useCallback(
    ({ label, value }) => (
      <Box display="flex" justifyContent="space-between">
        <Typography variant="caption" color="black" mr={1}>
          {label}:
        </Typography>
        <Typography variant="caption" color="black">
          {value ?? '--'}
        </Typography>
      </Box>
    ),
    [],
  )

  return (
    <>
      <svg width={width} height={height} ref={svgRef}>
        <rect
          x={margin.left}
          y={margin.top}
          width={innerWidth}
          height={innerHeight}
          fill="transparent"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseLeave}
        />
        <GridColumns
          top={margin.top}
          scale={xScale}
          height={innerHeight}
          pointerEvents="none"
        />
        <GridRows
          left={margin.left}
          scale={yScale}
          width={innerWidth}
          pointerEvents="none"
        />
        <AxisLeft
          scale={yScale}
          left={margin.left}
          label={yLabel}
          tickFormat={(d) => `${d}%`}
        />
        <Axis
          orientation="bottom"
          scale={xScale}
          top={innerHeight + margin.top}
          tickFormat={(d) => `${d}%`}
          label={xLabel}
        />
        <Group pointerEvents="none">
          {data.map((point) => (
            <Circle
              key={name(point)}
              cx={xScale(x(point))}
              cy={yScale(y(point))}
              r={4}
              fill={color(point)}
              fillOpacity={0.9}
              stroke={tooltipData === point ? 'black' : color(point)}
            />
          ))}
        </Group>
      </svg>
      {tooltipOpen && tooltipData && tooltipLeft != null && tooltipTop != null && (
        <TooltipWithBounds left={tooltipLeft + 10} top={tooltipTop + 10}>
          <Typography variant="body" fontWeight="bold" color={color(tooltipData)}>
            {tooltipData.name}
          </Typography>
          <Box mt={1}>
            <TooltipDataItem label={xLabel} value={x(tooltipData)} />
            <TooltipDataItem label={yLabel} value={y(tooltipData)} />
          </Box>
        </TooltipWithBounds>
      )}
    </>
  )
}

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object[]} props.data
 * @param {string} props.data[].name
 * @param {number} props.data[].x
 * @param {number} props.data[].y
 * @param {string} props.data[].color
 * @param {number} props.height
 * @param {string} props.xLabel
 * @param {string} props.yLabel
 */
export default function NegativeReviewsChart({ data, height, xLabel, yLabel }) {
  return (
    <Card style={{ width: '100%', position: 'relative', margin: 2 }}>
      <ParentSize>
        {({ width }) => (
          <Chart
            width={Math.max(0, width)}
            height={height}
            data={data}
            xLabel={xLabel}
            yLabel={yLabel}
          />
        )}
      </ParentSize>
    </Card>
  )
}
